// Generate all 15-minute intervals in a day (96 slots total)
// Params: none

import { getLocalTimeZone, today,  } from "@internationalized/date";

// Output: ["00:00", "00:15", ..., "23:45"]
export const timeIntervals = Array.from({ length: 96 }, (_, i) => {
  const totalMinutes = i * 15;
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const minutes = String(totalMinutes % 60).padStart(2, "0");
  return `${hours}:${minutes}`;
});

const toLocalISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Get the current datetime in YYYY-MM-DD HH:MM:SS format
// Params: none
// Output: "2025-09-05 17:32:45"
export const getCurrentDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Get the current time in HH:MM format
// Params: none
// Output: "14:07"
export const getCurrentTime = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

// Get the current date in YYYY-MM-DD format
// Params: none
// Output: "2025-08-19"
export const getCurrentDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Get the next nearest 15-minute interval from now
// Params: none
// Output: "14:15" (if current time is 14:07)
export const getPrev15MinTime = () => {
  const now = new Date();
  let hours = now.getHours();
  let minutes = now.getMinutes();

  minutes = Math.floor(minutes / 15) * 15;

  const hourStr = String(hours).padStart(2, "0");
  const minuteStr = String(minutes).padStart(2, "0");
  return `${hourStr}:${minuteStr}`;
};

export const getNextAvailable15MinTime = () => {
  const now = new Date();
  let hours = now.getHours();
  let minutes = now.getMinutes();

  // Always advance to the *next* slot, even if we're already on a boundary
  minutes = Math.floor(minutes / 15) * 15 + 15;

  if (minutes === 60) {
    minutes = 0;
    hours = (hours + 1) % 24;
  }

  const hourStr = String(hours).padStart(2, "0");
  const minuteStr = String(minutes).padStart(2, "0");
  return `${hourStr}:${minuteStr}`;
};

// Get the next nearest 15-minute interval based on an added duration (in hours)
// Params: duration (number, default=0) → hours to add
// Example: getNextAvailable15MinTimeBasedOnDuration(2)
// Output: "16:30" (if current time is 14:07)
export const getNextAvailable15MinTimeBasedOnDuration = (duration = 0) => {
  const now = new Date();
  now.setHours(now.getHours() + duration);

  let hours = now.getHours();
  let minutes = now.getMinutes();

  minutes = Math.ceil(minutes / 15) * 15;

  if (minutes === 60) {
    minutes = 0;
    hours = (hours + 1) % 24;
  }

  const hourStr = String(hours).padStart(2, "0");
  const minuteStr = String(minutes).padStart(2, "0");
  return `${hourStr}:${minuteStr}`;
};

// Get human-readable time difference between two times
// Params: start (string "HH:MM"), end (string "HH:MM")
// Example: getReadableTimeDiff("13:00", "14:45")
// Output: "1 hour 45 minutes"  |  short: "1h 45m"
export const getReadableTimeDiff = (start, end, format = "long") => {
  const [startHour, startMin] = start.split(":").map(Number);
  const [endHour, endMin] = end.split(":").map(Number);

  const startTotal = startHour * 60 + startMin;
  const endTotal = endHour * 60 + endMin;

  let diff = endTotal - startTotal;
  if (diff < 0) diff += 24 * 60; // wrap around midnight

  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;

  const parts = [];
  if (format === "short") {
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.length ? parts.join(" ") : "0m";
  }

  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);

  return parts.length ? parts.join(" ") : "0 minutes";
};

// Format a date object {year, month, day} into YYYY-MM-DD
// Params: {year: 2025, month: 8, day: 19}
// Output: "2025-08-19"
export const formatDateToYMD = (dateObj) => {
  return `${dateObj.year}-${String(dateObj.month).padStart(2, "0")}-${String(dateObj.day).padStart(2, "0")}`;
};

// Format a date string into readable format
// Params: "2025-08-19"
// Output: "August 19, 2025"
export const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

// Returns a human-friendly label for a past date string (YYYY-MM-DD).
// Within 3 days ago: "Yesterday · 9 Jul", "Last Wednesday · 8 Jul"
// Beyond 3 days ago: "Sat, 5 Jul 2026"
export const formatDateRelativePast = (dateStr) => {
  const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today - target) / 86400000);

  const shortDate = `${d} ${MONTHS_SHORT[m - 1]}`;

  if (diffDays === 0) return `Today · ${shortDate}`;
  if (diffDays === 1) return `Yesterday · ${shortDate}`;
  if (diffDays <= 3) return `Last ${DAYS[target.getDay()]} · ${shortDate}`;

  const dayName = DAYS[target.getDay()].slice(0, 3);
  return `${dayName}, ${shortDate} ${y}`;
};
// Within 3 days: "Today · 10 Jul", "Tomorrow · 11 Jul", "Wednesday · 12 Jul"
// Beyond 3 days: "Sat, 14 Jul 2026"
export const formatDateRelative = (dateStr) => {
  const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((target - today) / 86400000);

  const shortDate = `${d} ${MONTHS_SHORT[m - 1]}`;

  if (diffDays === 0) return `Today · ${shortDate}`;
  if (diffDays === 1) return `Tomorrow · ${shortDate}`;
  if (diffDays <= 3) return `${DAYS[target.getDay()]} · ${shortDate}`;

  const dayName = DAYS[target.getDay()].slice(0, 3);
  return `${dayName}, ${shortDate} ${y}`;
};

// Check if a meeting is currently active
// Params: _meets_date (YYYY-MM-DD), _start_time (HH:MM), _end_time (HH:MM)
// Output: true/false
export const isMeetingActive = (_meets_date, _start_time, _end_time) => {
  const now = new Date();
  const start = new Date(`${_meets_date}T${_start_time}`);
  const end = new Date(`${_meets_date}T${_end_time}`);
  return now >= start && now <= end;
};

// Check if a meeting has already ended
// Params: _meets_date (YYYY-MM-DD), _end_time (HH:MM)
// Output: true/false
export const isMeetingPast = (_meets_date, _end_time) => {
  const now = new Date();
  const end = new Date(`${_meets_date}T${_end_time}`);
  return now > end;
};

// Check if a meeting has not started yet
// Params: _meets_date (YYYY-MM-DD), _start_time (HH:MM)
// Output: true/false
export const isMeetingUpcoming = (_meets_date, _start_time) => {
  const now = new Date();
  const start = new Date(`${_meets_date}T${_start_time}`);
  return now < start;
};

// Get short month name from a YYYY-MM-DD string
// Params: _date_str = "2025-08-19"
// Output: "Aug"
export const getShortMonthName = (_date_str) => {
  const date = new Date(_date_str);
  return date.toLocaleString("en-US", { month: "short" });
};

// Get short month name from a YYYY-MM-DD string
// Params: _date_str = "2025-08-19"
// Output: ""Tuesday, 19 Aug 2025"
export const formatHumanDate = (_date_str) => {
  const date = new Date(_date_str);
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const isTimePassed = (timeStr) => {
  // timeStr expected as "HH:MM"
  const [hours, minutes] = timeStr.split(":").map(Number);

  // Create a Date for today with the provided time
  const now = new Date();
  const target = new Date();
  target.setHours(hours, minutes, 0, 0);

  return now >= target;
};

export function getWeekDays(referenceDate = new Date(), weekStartsOn = 1) {
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);

  const currentDay = today.getDay();
  const diff = (currentDay < weekStartsOn ? 7 : 0) + currentDay - weekStartsOn;

  const monday = new Date(today);
  monday.setDate(today.getDate() - diff);

  const todayString = toLocalISODate(new Date());

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);

    const isoDate = toLocalISODate(date);

    return {
      date,
      dayLabel: date.toLocaleDateString("en-US", { weekday: "short" }),
      dayNumber: date.getDate(),
      isToday: isoDate === todayString,
      isoDate,
    };
  });
}

export const formatDuration = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  return hours > 0 ? `${hours}:${mm}:${ss}` : `${minutes}:${ss}`;
};

// utils/dates.js
export const getCountdownLabel = (dateStr, timeStr) => {
  const now = new Date();
  const target = new Date(`${dateStr}T${timeStr}`);

  if (Number.isNaN(target.getTime())) return "";

  const diffMs = target - now;
  if (diffMs <= 0) return "Starting now";

  const totalMinutes = Math.round(diffMs / 60000);

  if (totalMinutes >= 1440) {
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);

    return `In ${days}d${hours > 0 ? ` ${hours}h` : ""}`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return `In ${parts.join(" ") || "0m"}`;
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const formatTime12h = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  if (isNaN(h)) return null;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

// Combines a relative day label with a formatted time.
// "Today, 11:00 AM" | "Tomorrow, 2:30 PM" | "Yesterday, 4:00 PM" | "Jul 13, 4:00 PM"
export const getRelativeDateTimeLabel = (dateStr, timeStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((target - today) / 86400000);

  const timeLabel = formatTime12h(timeStr);

  if (diffDays === 0) return `Today, ${timeLabel}`;
  if (diffDays === 1) return `Tomorrow, ${timeLabel}`;
  if (diffDays === -1) return `Yesterday, ${timeLabel}`;

  return `${MONTHS_SHORT[m - 1]} ${d}, ${timeLabel}`;
};

// Extracts "YYYY-MM-DD" and "HH:MM" from a full timestamp like
// "2026-07-17 11:00:00.000000+02:00"
export const splitDateTimeString = (fullTimestamp) => {
  const [datePart, timePart] = fullTimestamp.split(" ");
  return { date: datePart, time: timePart.slice(0, 5) };
};

// export function getPastDateRange(monthsAgo = 3) {
//   const format = (d) =>
//     `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

//   const end = new Date();
//   const start = new Date();
//   start.setMonth(start.getMonth() - monthsAgo);

//   return [format(start), format(end)];
// }

export function getPastDateRange(monthsAgo = 3, padEndDays = 1) {
  const format = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const end = new Date();
  end.setDate(end.getDate() + padEndDays);

  const start = new Date();
  start.setMonth(start.getMonth() - monthsAgo);

  return [format(start), format(end)];
}

export const getCurrentWeekDateRange = (referenceDate = new Date(), weekStartsOn = 1) => {
  const days = getWeekDays(referenceDate, weekStartsOn);
  return [days[0].isoDate, days[6].isoDate];
};

export function defaultAdminRange() {
  const end = today(getLocalTimeZone());
  return { start: end.subtract({ days: 6 }), end };
}

/** CalendarDate → "YYYY-MM-DD" for the API. toString() already yields this. */
export function toApiDate(calendarDate) {
  return calendarDate ? calendarDate.toString() : null;
}

/** Range → { start_date, end_date } query params. */
export function rangeToParams(range) {
  if (!range?.start || !range?.end) return {};
  return { start_date: toApiDate(range.start), end_date: toApiDate(range.end) };
}

const _WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const _MONTHS   = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Accepts a @internationalized/date value, an ISO string, or a native Date. */
export function toJsDate(date) {
  if (!date) return null;
  let jsDate;
  if (typeof date === "string") {
    jsDate = new Date(date);
  } else if (date.year && date.month && date.day) {
    jsDate = new Date(date.year, date.month - 1, date.day);
  } else {
    jsDate = new Date(date);
  }
  return isNaN(jsDate.getTime()) ? null : jsDate;
}

/** "Wednesday, 6 Aug 2026" */
export function formatEventDate(date) {
  const jsDate = toJsDate(date);
  if (!jsDate) return null;
  return `${_WEEKDAYS[jsDate.getDay()]}, ${jsDate.getDate()} ${_MONTHS[jsDate.getMonth()]} ${jsDate.getFullYear()}`;
}

/** "Wed, 6 Aug" — compact variant for tight layouts. */
export function formatEventDateShort(date) {
  const jsDate = toJsDate(date);
  if (!jsDate) return null;
  return `${_WEEKDAYS[jsDate.getDay()].slice(0, 3)}, ${jsDate.getDate()} ${_MONTHS[jsDate.getMonth()]}`;
}

// export function getPastDateRange(monthsAgo = 3, padEndDays = 1) {
//   const format = (d) =>
//     `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

//   const end = new Date();
//   end.setDate(end.getDate() + padEndDays);

//   const start = new Date();
//   start.setMonth(start.getMonth() - monthsAgo);

//   return [format(start), format(end)];
// }

export const getLoopsDateRange = (monthsBack = 3, monthsForward = 1) => {
  const start = new Date();
  start.setMonth(start.getMonth() - monthsBack);

  const end = new Date();
  end.setMonth(end.getMonth() + monthsForward);

  return [toLocalISODate(start), toLocalISODate(end)];
};

// Meetings use the application's Africa/Johannesburg timezone (UTC+02:00).
export const isMeetingEnded = (meeting) => {
  if (meeting?.completed) return true;
  const end = meeting?.end_time?.real || meeting?.end_time?.set;
  if (!meeting?.meeting_date || !end) return false;
  return Date.now() >= new Date(`${meeting.meeting_date}T${end}+02:00`).getTime();
};
